import { useState, useCallback } from "react";
import {
  ActionIcon,
  Button,
  Flex,
  Loader,
  Menu,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { apiService } from "../../services/api.service";
import { translateIdentity, type StaticTextTranslator } from "../../utils";
import { SESSION_MENU_TEST_IDS } from "./constants";

interface SessionMenuProps {
  sessionId: string;
  candidateEmail?: string;
  /** Optional translation function. Falls back to identity (English). */
  translate?: StaticTextTranslator;
}

export function SessionMenu({
  sessionId,
  candidateEmail,
  translate = translateIdentity,
}: SessionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");

  const doSave = useCallback(
    async (emailToUse: string) => {
      setIsSaving(true);
      try {
        await apiService.saveProgress(sessionId, emailToUse);
        notifications.show({
          title: translate("Progress Saved"),
          message: translate(
            "A resume link has been sent to your email. You can return to complete your interview later.",
          ),
          color: "green",
        });
        setShowEmailModal(false);
        setEmail("");
      } catch {
        notifications.show({
          title: translate("Error"),
          message: translate("Failed to save progress. Please try again."),
          color: "red",
        });
      } finally {
        setIsSaving(false);
      }
    },
    [sessionId, translate],
  );

  const handleSaveProgress = useCallback(() => {
    setIsOpen(false);
    if (candidateEmail) {
      doSave(candidateEmail);
    } else {
      setShowEmailModal(true);
    }
  }, [candidateEmail, doSave]);

  const handleEmailSubmit = useCallback(() => {
    const trimmed = email.trim();
    if (!trimmed) {
      return;
    }
    doSave(trimmed);
  }, [email, doSave]);

  const handleRestartClick = useCallback(() => {
    setIsOpen(false);
    setShowRestartConfirm(true);
  }, []);

  const handleRestartConfirm = useCallback(async () => {
    setShowRestartConfirm(false);
    try {
      await apiService.endInterview(sessionId);
    } catch {
      // Best effort — session will be cleaned up by cron if this fails
    }
    window.location.reload();
  }, [sessionId]);

  return (
    <>
      <Menu
        offset={4}
        onChange={setIsOpen}
        opened={isOpen}
        position="bottom-end"
        shadow="md"
        width={220}
      >
        <Menu.Target>
          <ActionIcon
            aria-label={translate("Session options")}
            data-testid={SESSION_MENU_TEST_IDS.menuButton}
            variant="subtle"
            color="gray"
          >
            {isSaving ? (
              <Loader color="purple.5" size="xs" />
            ) : (
              <Text size="lg" fw={700} lh={1}>
                &#8942;
              </Text>
            )}
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            data-testid={SESSION_MENU_TEST_IDS.saveProgress}
            disabled={isSaving}
            onClick={handleSaveProgress}
          >
            <Text size="sm">{translate("Save progress")}</Text>
          </Menu.Item>
          <Menu.Item
            data-testid={SESSION_MENU_TEST_IDS.restartInterview}
            onClick={handleRestartClick}
          >
            <Text size="sm">{translate("Restart interview")}</Text>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      {/* Email modal — shown when saving without a known email */}
      <Modal
        centered
        data-testid={SESSION_MENU_TEST_IDS.emailModal}
        onClose={() => setShowEmailModal(false)}
        opened={showEmailModal}
        title={translate("Save Your Progress")}
      >
        <Stack gap="md">
          <Text>
            {translate(
              "Enter your email address and we will send you a link to resume your interview later.",
            )}
          </Text>
          <TextInput
            label={translate("Email")}
            placeholder="your@email.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleEmailSubmit();
              }
            }}
            required
          />
          <Button onClick={handleEmailSubmit} loading={isSaving} fullWidth>
            {translate("Save & Send Link")}
          </Button>
        </Stack>
      </Modal>

      {/* Restart confirmation modal */}
      <Modal
        centered
        data-testid={SESSION_MENU_TEST_IDS.restartConfirmModal}
        onClose={() => setShowRestartConfirm(false)}
        opened={showRestartConfirm}
        title={translate("Start over?")}
      >
        <Text>{translate("Your previous answers will be cleared.")}</Text>
        <Flex gap="sm" mt="md">
          <Button
            data-testid={SESSION_MENU_TEST_IDS.restartCancelButton}
            onClick={() => setShowRestartConfirm(false)}
            variant="outline"
          >
            {translate("Cancel")}
          </Button>
          <Button
            color="red"
            data-testid={SESSION_MENU_TEST_IDS.restartConfirmButton}
            onClick={handleRestartConfirm}
          >
            {translate("Start over")}
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
